import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { isXlsxBuffer, decodeText } from '../DocumentViewer/spreadsheetBytes';
import { FortuneCell, FortuneCellData, FortuneSheet } from './types';
import { MIN_ROW_HEIGHT } from './fileTypes';

const MIN_ROWS = 50;
const MIN_COLS = 26;

// Returns the SSF format string for a cell, or 'General' when absent/generic.
const numFmt = (z?: string | number): string => (typeof z === 'string' && z !== 'General' ? z : 'General');

/**
 * Convert one SheetJS worksheet into Fortune-Sheet celldata, preserving formulas.
 * Serves the CSV path only — .xlsx import/export goes through exceljs (below) for
 * full cell-style fidelity (SheetJS's community build can't read/write cell.s).
 * Exported for unit tests.
 */
export const worksheetToCelldata = (
    ws: XLSX.WorkSheet,
): { celldata: FortuneCellData[]; rows: number; cols: number; config: FortuneSheet['config'] } => {
    const celldata: FortuneCellData[] = [];
    const ref = ws['!ref'];
    if (!ref) return { celldata, rows: MIN_ROWS, cols: MIN_COLS, config: {} };

    const range = XLSX.utils.decode_range(ref);
    // Iterate only the cells that actually exist — looping the full !ref
    // rectangle explodes to millions of iterations on sparse/templated sheets.
    Object.keys(ws).forEach((addr) => {
        if (addr.startsWith('!')) return;
        const cell = ws[addr] as XLSX.CellObject;
        const { r, c } = XLSX.utils.decode_cell(addr);

        const fcell: FortuneCell = {};
        // SheetJS stores formulas without the leading '='; Fortune-Sheet wants it.
        if (cell.f) fcell.f = `=${cell.f}`;

        if (cell.t === 'n') {
            fcell.v = cell.v as number;
            fcell.m = cell.w ?? String(cell.v);
            fcell.ct = { t: 'n', fa: numFmt(cell.z) };
        } else if (cell.t === 'b') {
            fcell.v = cell.v as boolean;
            fcell.m = cell.w ?? (cell.v ? 'TRUE' : 'FALSE');
            fcell.ct = { t: 'b', fa: 'General' };
        } else if (cell.t === 'e') {
            fcell.v = (cell.w as string) ?? String(cell.v);
            fcell.m = fcell.v as string;
            fcell.ct = { t: 'e', fa: 'General' };
        } else if (cell.v !== undefined && cell.v !== null) {
            fcell.v = cell.v == null ? '' : String(cell.v);
            fcell.m = cell.w ?? String(fcell.v);
            fcell.ct = { t: 's', fa: '@' };
        }

        if (fcell.v === undefined && !fcell.f) return;
        celldata.push({ r, c, v: fcell });
    });

    const config: NonNullable<FortuneSheet['config']> = {};
    const merges = ws['!merges'];
    if (merges?.length) {
        config.merge = {};
        merges.forEach((m) => {
            config.merge![`${m.s.r}_${m.s.c}`] = { r: m.s.r, c: m.s.c, rs: m.e.r - m.s.r + 1, cs: m.e.c - m.s.c + 1 };
        });
    }
    const cols = ws['!cols'];
    if (cols?.length) {
        config.columnlen = {};
        cols.forEach((col, i) => {
            if (!col) return;
            const px = col.wpx ?? (col.wch != null ? Math.round(col.wch * 7 + 5) : undefined);
            if (px != null) config.columnlen![String(i)] = px;
        });
    }
    const rowsCfg = ws['!rows'];
    if (rowsCfg?.length) {
        config.rowlen = {};
        rowsCfg.forEach((row, i) => {
            if (!row) return;
            const px = row.hpx ?? (row.hpt != null ? Math.round((row.hpt * 96) / 72) : undefined);
            // Clamp UP to the floor — a shorter authored row height (e.g. from a
            // dense source workbook) must not render shorter than MIN_ROW_HEIGHT.
            if (px != null) config.rowlen![String(i)] = Math.max(px, MIN_ROW_HEIGHT);
        });
    }

    return {
        celldata,
        rows: Math.max(MIN_ROWS, range.e.r + 1),
        cols: Math.max(MIN_COLS, range.e.c + 1),
        config,
    };
};

const buildSheet = (
    name: string,
    index: number,
    celldata: FortuneCellData[],
    rows: number,
    cols: number,
    config?: FortuneSheet['config'],
    defaultRowHeight?: number,
): FortuneSheet => ({
    name,
    id: String(index),
    order: index,
    status: index === 0 ? 1 : 0,
    celldata,
    row: rows,
    column: cols,
    ...(config && Object.keys(config).length ? { config } : {}),
    ...(defaultRowHeight != null ? { defaultRowHeight } : {}),
});

const parseCsv = (text: string): FortuneSheet[] => {
    // Capture raw CSV tokens via PapaParse BEFORE SheetJS numeric coercion.
    // XLSX.read converts 17+ digit values to rounded JS floats, making cell.w
    // unreliable for recovering the original token; the raw PapaParse strings are exact.
    const rawByAddr = new Map<string, string>();
    Papa.parse<string[]>(text, { skipEmptyLines: false }).data.forEach((row, r) => {
        row.forEach((rawVal, c) => {
            if (rawVal !== '' && rawVal != null) {
                rawByAddr.set(XLSX.utils.encode_cell({ r, c }), rawVal);
            }
        });
    });

    // Parse CSV with the same engine as xlsx so numbers, dates and formats are
    // detected identically. `decodeText` (caller) already handled UTF-16/BOM.
    const wb = XLSX.read(text, { type: 'string', cellNF: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (ws) {
        // Keep long numeric IDs (>=16 digits, e.g. account/UTR numbers) as text —
        // a JS double can't represent them exactly, so typing them as numbers corrupts them.
        Object.keys(ws).forEach((addr) => {
            if (addr.startsWith('!')) return;
            const cell = ws[addr] as XLSX.CellObject;
            const raw = rawByAddr.get(addr) ?? '';
            const digits = raw.replace(/[,\s]/g, '');
            if (cell.t === 'n' && /^\d{16,}$/.test(digits)) {
                cell.t = 's';
                cell.v = raw;
                delete cell.z;
            }
        });
    }
    const { celldata, rows, cols, config } = ws
        ? worksheetToCelldata(ws)
        : { celldata: [], rows: MIN_ROWS, cols: MIN_COLS, config: {} as FortuneSheet['config'] };
    return [buildSheet('Sheet1', 0, celldata, rows, cols, config)];
};

// ============================================================================
// .xlsx IMPORT — exceljs (full font/fill/alignment/border read fidelity;
// SheetJS's community build only ever surfaces fill color, see git history).
// ============================================================================

// Border style name <-> Fortune-Sheet/luckysheet numeric code, shared both directions.
const BORDER_STYLE_TO_CODE: Record<string, number> = {
    thin: 1,
    hair: 2,
    dotted: 3,
    dashed: 4,
    dashDot: 5,
    dashDotDot: 6,
    double: 7,
    medium: 8,
    mediumDashed: 9,
    mediumDashDot: 10,
    mediumDashDotDot: 11,
    slantDashDot: 12,
    thick: 13,
};
const BORDER_CODE_TO_STYLE: Record<number, ExcelJS.BorderStyle> = Object.fromEntries(
    Object.entries(BORDER_STYLE_TO_CODE).map(([style, code]) => [code, style as ExcelJS.BorderStyle]),
);

type BorderSideValue = { style: number; color: string };
type BorderInfoEntry = {
    rangeType: 'cell';
    value: { row_index: number; col_index: number; l?: BorderSideValue; r?: BorderSideValue; t?: BorderSideValue; b?: BorderSideValue };
};

// exceljs colors are ARGB (8-hex, leading alpha). Real Excel theme colors arrive as
// {theme, tint} with no .argb — skip gracefully rather than crash (known limitation).
const argbToHex = (argb?: string): string | undefined => (argb ? `#${argb.slice(-6).toUpperCase()}` : undefined);
const hexToArgb = (hex?: string): string | undefined => (hex ? `FF${hex.replace(/^#/, '').slice(-6).toUpperCase()}` : undefined);

// Excel/exceljs date serials count days since 1899-12-30 (the 1900 date system;
// the rarer 1904 system isn't handled — not something this editor's users hit).
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const dateToSerial = (d: Date): number => (d.getTime() - EXCEL_EPOCH_MS) / 86400000;

/** Flatten an exceljs cell value (formula/error/richText/hyperlink/date wrapper) to a primitive. */
const resolveExceljsValue = (raw: ExcelJS.CellValue): { value: string | number | boolean; isError?: boolean } | undefined => {
    if (raw == null) return undefined;
    if (raw instanceof Date) return { value: dateToSerial(raw) };
    if (typeof raw === 'object') {
        if ('error' in raw) return { value: raw.error, isError: true };
        if ('richText' in raw) return { value: raw.richText.map((rt) => rt.text).join('') };
        if ('hyperlink' in raw) return { value: raw.text };
        if ('formula' in raw || 'sharedFormula' in raw) {
            return raw.result != null ? resolveExceljsValue(raw.result as ExcelJS.CellValue) : undefined;
        }
        return undefined;
    }
    return { value: raw };
};

const assignFcellValue = (fcell: FortuneCell, cell: ExcelJS.Cell): void => {
    const raw = cell.value;
    if (raw != null && typeof raw === 'object' && !(raw instanceof Date)) {
        if ('formula' in raw) fcell.f = `=${raw.formula}`;
        else if ('sharedFormula' in raw && raw.formula) fcell.f = `=${raw.formula}`;
    }

    const resolved = resolveExceljsValue(raw);
    if (!resolved) return;
    const { value, isError } = resolved;
    if (isError) {
        fcell.v = value as string;
        fcell.m = value as string;
        fcell.ct = { t: 'e', fa: 'General' };
    } else if (typeof value === 'boolean') {
        fcell.v = value;
        fcell.m = value ? 'TRUE' : 'FALSE';
        fcell.ct = { t: 'b', fa: 'General' };
    } else if (typeof value === 'number') {
        fcell.v = value;
        fcell.m = cell.numFmt ? XLSX.SSF.format(cell.numFmt, value) : String(value);
        fcell.ct = { t: 'n', fa: numFmt(cell.numFmt) };
    } else {
        fcell.v = value;
        fcell.m = value;
        fcell.ct = { t: 's', fa: '@' };
    }
};

/** Map exceljs font/fill/alignment onto a Fortune-Sheet cell. Only sets present fields. */
const applyExceljsStyle = (fcell: FortuneCell, cell: ExcelJS.Cell): void => {
    const font = cell.font;
    if (font?.bold) fcell.bl = 1;
    if (font?.italic) fcell.it = 1;
    if (font?.underline && font.underline !== 'none') fcell.un = 1;
    if (font?.strike) fcell.cl = 1;
    if (font?.name) fcell.ff = font.name;
    if (font?.size) fcell.fs = font.size;
    const fc = argbToHex(font?.color?.argb);
    if (fc) fcell.fc = fc;

    const fill = cell.fill;
    if (fill?.type === 'pattern' && fill.pattern === 'solid') {
        const bg = argbToHex(fill.fgColor?.argb);
        if (bg) fcell.bg = bg;
    }

    const alignment = cell.alignment;
    if (alignment?.horizontal === 'left') fcell.ht = 1;
    else if (alignment?.horizontal === 'center') fcell.ht = 0;
    else if (alignment?.horizontal === 'right') fcell.ht = 2;
    if (alignment?.vertical === 'top') fcell.vt = 1;
    else if (alignment?.vertical === 'middle') fcell.vt = 0;
    else if (alignment?.vertical === 'bottom') fcell.vt = 2;
    if (alignment?.wrapText) fcell.tb = '2';
};

const BORDER_SIDES = [
    ['top', 't'],
    ['left', 'l'],
    ['bottom', 'b'],
    ['right', 'r'],
] as const;

/** Collect a cell's borders into a Fortune-Sheet config.borderInfo entry, or undefined if none. */
const collectBorderEntry = (row_index: number, col_index: number, border?: Partial<ExcelJS.Borders>): BorderInfoEntry | undefined => {
    if (!border) return undefined;
    const value: BorderInfoEntry['value'] = { row_index, col_index };
    let any = false;
    BORDER_SIDES.forEach(([side, key]) => {
        const b = border[side];
        const code = b?.style ? BORDER_STYLE_TO_CODE[b.style] : undefined;
        if (!code) return;
        value[key] = { style: code, color: argbToHex(b?.color?.argb) ?? '#000000' };
        any = true;
    });
    return any ? { rangeType: 'cell', value } : undefined;
};

type MergeMaster = { r: number; c: number; rs: number; cs: number };

// Exported for unit tests (real-bytes import tests are CI-skipped — see converters.test.ts).
export const exceljsWorksheetToCelldata = (
    ws: ExcelJS.Worksheet,
): { celldata: FortuneCellData[]; rows: number; cols: number; config: FortuneSheet['config']; defaultRowHeight?: number } => {
    const celldata: FortuneCellData[] = [];
    const borderInfo: BorderInfoEntry[] = [];
    const rowlen: Record<string, number> = {};
    let maxR = 0;
    let maxC = 0;

    const config: NonNullable<FortuneSheet['config']> = {};
    // FortuneSheet renders a merge's rowspan/colspan off the CELL's own `mc` field, not
    // config.merge — so every cell in a merge range needs `mc` (master: full {r,c,rs,cs};
    // covered: just the master's {r,c}), including cells `eachCell({includeEmpty:false})`
    // would otherwise skip because they carry no value.
    const masterMc = new Map<string, MergeMaster>();
    const coveredMc = new Map<string, { r: number; c: number }>();
    const merges = ws.model.merges;
    if (merges?.length) {
        config.merge = {};
        merges.forEach((rangeStr) => {
            const { s, e } = XLSX.utils.decode_range(rangeStr);
            const master: MergeMaster = { r: s.r, c: s.c, rs: e.r - s.r + 1, cs: e.c - s.c + 1 };
            config.merge![`${s.r}_${s.c}`] = master;
            masterMc.set(`${s.r}_${s.c}`, master);
            // A merge can reach past the last valued cell — extend the sheet bounds to fit.
            maxR = Math.max(maxR, e.r);
            maxC = Math.max(maxC, e.c);
            for (let rr = s.r; rr <= e.r; rr++) {
                for (let cc = s.c; cc <= e.c; cc++) {
                    if (rr === s.r && cc === s.c) continue;
                    coveredMc.set(`${rr}_${cc}`, { r: s.r, c: s.c });
                }
            }
        });
    }

    const emitted = new Set<string>();

    // includeEmpty:true so a row's custom height is collected even when the row has
    // no cells at all (eachCell below still skips empty cells for cell-value purposes).
    ws.eachRow({ includeEmpty: true }, (row, rn) => {
        // Clamp UP to the floor — a shorter authored row height (e.g. from a
        // dense source workbook) must not render shorter than MIN_ROW_HEIGHT.
        if (row.height != null) rowlen[String(rn - 1)] = Math.max(Math.round((row.height * 96) / 72), MIN_ROW_HEIGHT);

        row.eachCell({ includeEmpty: false }, (cell, cn) => {
            const r = rn - 1;
            const c = cn - 1;
            const key = `${r}_${c}`;

            // exceljs proxies the MASTER's value onto every slave cell in a merge
            // (cell.value/isMerged both report as if the slave held the master's
            // content) — reading it as the slave's own value would duplicate the
            // master's text across the whole merge. A slave carries `mc` only.
            if (cell.isMerged && cell.master !== cell) {
                const covered = coveredMc.get(key);
                if (covered) {
                    celldata.push({ r, c, v: { mc: covered } });
                    emitted.add(key);
                }
                return;
            }

            const fcell: FortuneCell = {};
            assignFcellValue(fcell, cell);
            if (fcell.v === undefined && !fcell.f) return;

            applyExceljsStyle(fcell, cell);
            const border = collectBorderEntry(r, c, cell.border);
            if (border) borderInfo.push(border);

            const master = masterMc.get(key);
            if (master) fcell.mc = master;

            maxR = Math.max(maxR, r);
            maxC = Math.max(maxC, c);
            celldata.push({ r, c, v: fcell });
            emitted.add(key);
        });
    });

    // Backfill merge master/covered cells eachCell skipped (no value of their own) —
    // an empty merged block still needs its master (and every covered cell) present.
    masterMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        celldata.push({ r: master.r, c: master.c, v: { mc: master } });
    });
    coveredMc.forEach((master, key) => {
        if (emitted.has(key)) return;
        const [r, c] = key.split('_').map(Number);
        celldata.push({ r, c, v: { mc: master } });
    });

    if (borderInfo.length) config.borderInfo = borderInfo;
    if (Object.keys(rowlen).length) config.rowlen = rowlen;

    const columnlen: Record<string, number> = {};
    // exceljs types columns as Column[], but the getter returns its internal
    // _columns, which is null for sheets with no <cols> block (empty divider tabs).
    (ws.columns ?? []).forEach((col, i) => {
        if (col?.width != null) columnlen[String(i)] = Math.round(col.width * 7 + 5);
    });
    if (Object.keys(columnlen).length) config.columnlen = columnlen;

    const defaultRowHeight = ws.properties?.defaultRowHeight != null ? Math.round((ws.properties.defaultRowHeight * 96) / 72) : undefined;

    return {
        celldata,
        rows: Math.max(MIN_ROWS, maxR + 1),
        cols: Math.max(MIN_COLS, maxC + 1),
        config,
        defaultRowHeight,
    };
};

const parseXlsxAsync = async (buffer: ArrayBuffer): Promise<FortuneSheet[]> => {
    const wb = new ExcelJS.Workbook();
    // exceljs's own .d.ts types this as Node's Buffer, but its browser build (and this
    // Node runtime) both accept a plain ArrayBuffer directly — verified empirically.
    await wb.xlsx.load(buffer as unknown as Buffer);
    return wb.worksheets.map((ws, i) => {
        const { celldata, rows, cols, config, defaultRowHeight } = exceljsWorksheetToCelldata(ws);
        return buildSheet(ws.name, i, celldata, rows, cols, config, defaultRowHeight);
    });
};

/**
 * Parse fetched file bytes into Fortune-Sheet sheets. Detects XLSX by content
 * (ZIP magic bytes) so a mislabelled .csv that is really an .xlsx still opens,
 * mirroring DocumentViewer's detection. When the MIME type doesn't confirm CSV
 * (empty/wrong — common for local file pickers on some OS/browsers), fall back
 * to the file's extension before defaulting to XLSX, so a real local .csv with
 * a blank/incorrect `type` doesn't get mis-parsed as a workbook.
 */
export const fileBytesToFortuneSheets = async (buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<FortuneSheet[]> => {
    if (isXlsxBuffer(buffer)) return parseXlsxAsync(buffer);
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType === 'text/csv' || mimeType === 'application/csv' || ext === 'csv') return parseCsv(decodeText(buffer));
    return parseXlsxAsync(buffer);
};

/**
 * Read a File object into FortuneSheet[]. Use this in the import flow
 * (xlsx/xls/csv) to go from a user-selected file to sheets ready for
 * SheetEditorDrawer's `sheets` prop.
 */
export const importFileToSheets = async (file: File): Promise<FortuneSheet[]> => {
    const buffer = await file.arrayBuffer();
    return fileBytesToFortuneSheets(buffer, file.type, file.name);
};

/** Normalise a Fortune-Sheet sheet (celldata or 2D data) to a flat cell list. */
const getCells = (sheet: FortuneSheet): FortuneCellData[] => {
    if (sheet.celldata && sheet.celldata.length) return sheet.celldata;
    if (sheet.data) {
        const out: FortuneCellData[] = [];
        sheet.data.forEach((row, r) => {
            row?.forEach((cell, c) => {
                if (cell == null) return;
                out.push({ r, c, v: cell });
            });
        });
        return out;
    }
    return [];
};

/**
 * Normalise a sheet to Fortune-Sheet's `celldata` init form. `<Workbook data>`
 * builds the grid from `celldata`, but `getAllSheets()` (what save persists)
 * returns only the runtime 2D `data` plus selection state — so re-feeding a
 * saved sheet to the editor renders an empty grid. Rebuild `celldata` from
 * `data`, drop the runtime-only `data`/`luckysheet_select_save`, and keep
 * everything else (`config` merges/widths/styles, `frozen`, …). No-op for
 * sheets that already carry `celldata` (the import path).
 */
export const toCelldataSheet = (sheet: FortuneSheet): FortuneSheet => {
    const { data, luckysheet_select_save, ...rest } = sheet;
    return { ...rest, celldata: getCells(sheet) };
};

const cellRaw = (v: FortuneCellData['v']): { value: string | number | boolean | null; formula?: string } => {
    if (v == null) return { value: null };
    if (typeof v === 'object') return { value: v.v ?? null, formula: v.f };
    return { value: v };
};

// ============================================================================
// .xlsx EXPORT — exceljs (full font/fill/alignment/border write fidelity).
// ============================================================================

const buildExceljsFont = (v: FortuneCell): Partial<ExcelJS.Font> | undefined => {
    const font: Partial<ExcelJS.Font> = {};
    if (v.bl) font.bold = true;
    if (v.it) font.italic = true;
    if (v.un) font.underline = true;
    if (v.cl) font.strike = true;
    if (v.ff != null) font.name = String(v.ff);
    if (v.fs != null) font.size = v.fs;
    const argb = hexToArgb(v.fc);
    if (argb) font.color = { argb };
    return Object.keys(font).length ? font : undefined;
};

const buildExceljsFill = (v: FortuneCell): ExcelJS.Fill | undefined => {
    const argb = hexToArgb(v.bg);
    return argb ? { type: 'pattern', pattern: 'solid', fgColor: { argb } } : undefined;
};

const buildExceljsAlignment = (v: FortuneCell): Partial<ExcelJS.Alignment> | undefined => {
    const alignment: Partial<ExcelJS.Alignment> = {};
    if (v.ht === 1) alignment.horizontal = 'left';
    else if (v.ht === 0) alignment.horizontal = 'center';
    else if (v.ht === 2) alignment.horizontal = 'right';
    if (v.vt === 1) alignment.vertical = 'top';
    else if (v.vt === 0) alignment.vertical = 'middle';
    else if (v.vt === 2) alignment.vertical = 'bottom';
    if (v.tb === '2') alignment.wrapText = true;
    return Object.keys(alignment).length ? alignment : undefined;
};

// Only handles rangeType 'cell' entries (what our own import produces, and a
// single-cell border edit). A multi-cell 'range' rangeType from FortuneSheet's
// own border-drawing UI isn't covered — noted as a known limitation.
const buildBorderLookup = (borderInfo: unknown): Map<string, Partial<ExcelJS.Borders>> => {
    const map = new Map<string, Partial<ExcelJS.Borders>>();
    ((borderInfo as BorderInfoEntry[] | undefined) ?? []).forEach((entry) => {
        if (entry?.rangeType !== 'cell' || !entry.value) return;
        const { row_index, col_index, l, r, t, b } = entry.value;
        const sides: Partial<ExcelJS.Borders> = {};
        if (l) sides.left = { style: BORDER_CODE_TO_STYLE[l.style], color: { argb: hexToArgb(l.color) } };
        if (r) sides.right = { style: BORDER_CODE_TO_STYLE[r.style], color: { argb: hexToArgb(r.color) } };
        if (t) sides.top = { style: BORDER_CODE_TO_STYLE[t.style], color: { argb: hexToArgb(t.color) } };
        if (b) sides.bottom = { style: BORDER_CODE_TO_STYLE[b.style], color: { argb: hexToArgb(b.color) } };
        if (Object.keys(sides).length) map.set(`${row_index}_${col_index}`, sides);
    });
    return map;
};

const addExceljsWorksheet = (wb: ExcelJS.Workbook, sheet: FortuneSheet, name: string): void => {
    const ws = wb.addWorksheet(name);
    const borders = buildBorderLookup(sheet.config?.borderInfo);

    getCells(sheet).forEach(({ r, c, v }) => {
        const { value, formula } = cellRaw(v);
        if (value == null && !formula) return;

        const xc = ws.getCell(r + 1, c + 1);
        if (formula) {
            xc.value = { formula: formula.replace(/^=/, ''), result: value ?? undefined };
        } else {
            xc.value = value;
        }

        const fcell = v && typeof v === 'object' ? (v as FortuneCell) : undefined;
        // Carry the SSF number format through to the exported workbook.
        if (fcell?.ct?.fa && fcell.ct.fa !== 'General') xc.numFmt = fcell.ct.fa;

        if (fcell) {
            const font = buildExceljsFont(fcell);
            if (font) xc.font = font;
            const fill = buildExceljsFill(fcell);
            if (fill) xc.fill = fill;
            const alignment = buildExceljsAlignment(fcell);
            if (alignment) xc.alignment = alignment;
        }
        const border = borders.get(`${r}_${c}`);
        if (border) xc.border = border;
    });

    if (sheet.config?.merge) {
        Object.values(sheet.config.merge).forEach((m) => {
            ws.mergeCells(m.r + 1, m.c + 1, m.r + m.rs, m.c + m.cs);
        });
    }
    if (sheet.config?.columnlen) {
        Object.entries(sheet.config.columnlen).forEach(([i, px]) => {
            ws.getColumn(Number(i) + 1).width = px / 7;
        });
    }
    if (sheet.config?.rowlen) {
        Object.entries(sheet.config.rowlen).forEach(([i, px]) => {
            ws.getRow(Number(i) + 1).height = (px * 72) / 96;
        });
    }
};

// Excel forbids these characters in sheet names and caps length at 31.
const sanitizeSheetName = (name: string, index: number): string => {
    const cleaned = (name || `Sheet${index + 1}`)
        .replace(/[\\/?*[\]:]/g, ' ')
        .slice(0, 31)
        .trim();
    return cleaned || `Sheet${index + 1}`;
};

// Excel rejects duplicate sheet names (case-insensitive); suffix collisions
// while keeping within the 31-char cap so addWorksheet never throws.
const uniqueSheetName = (base: string, used: Set<string>): string => {
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
        const suffix = ` (${n})`;
        name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
        n += 1;
    }
    used.add(name.toLowerCase());
    return name;
};

const triggerDownload = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const stripExtension = (fileName: string): string => fileName.replace(/\.(xlsx|xls|csv)$/i, '');

// Prevent CSV formula injection: a cell beginning with = + - @ can execute when
// the exported file is opened in Excel/Sheets. Prefix such strings with a quote.
const sanitizeCsvCell = (value: string | number | boolean): string | number | boolean =>
    typeof value === 'string' && /^[=+\-@]/.test(value) ? `'${value}` : value;

/** Export every sheet to a single .xlsx workbook and download it. */
export const downloadAsXlsx = async (sheets: FortuneSheet[], fileName: string): Promise<void> => {
    const wb = new ExcelJS.Workbook();
    const usedNames = new Set<string>();
    sheets.forEach((sheet, i) => {
        const name = uniqueSheetName(sanitizeSheetName(sheet.name, i), usedNames);
        addExceljsWorksheet(wb, sheet, name);
    });
    const out = await wb.xlsx.writeBuffer();
    triggerDownload(
        new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${stripExtension(fileName)}.xlsx`,
    );
};

/** Export a single sheet (the active one) to .csv and download it. */
export const downloadAsCsv = (sheet: FortuneSheet, fileName: string): void => {
    const cells = getCells(sheet);
    let maxR = 0;
    let maxC = 0;
    cells.forEach(({ r, c }) => {
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);
    });
    const aoa: (string | number | boolean)[][] = Array.from({ length: maxR + 1 }, () => Array.from({ length: maxC + 1 }, () => ''));
    cells.forEach(({ r, c, v }) => {
        // Use the raw value, not the display string `m` — for number-formatted
        // cells `m` carries separators/currency (e.g. "1,234") which corrupts CSV.
        const { value } = cellRaw(v);
        aoa[r][c] = sanitizeCsvCell((value ?? '') as string | number | boolean);
    });
    const csv = Papa.unparse(aoa);
    triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${stripExtension(fileName)}.csv`);
};
